import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Plus, Minus, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { animalData } from '../../data/animals';
import { useCart } from '../../context/CartContext';
import { toast } from 'sonner';

const fmt = (p) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: p % 1 === 0 ? 0 : 2 }).format(p);

function FiberglassSlider() {
  const { addItem } = useCart();
  const scrollRef = useRef(null);
  const [selected, setSelected] = useState(null);
  const { fiberglass } = animalData.props;

  const scroll = (dir) => {
    scrollRef.current?.scrollBy({ left: dir * 280, behavior: 'smooth' });
  };

  const handleAdd = () => {
    if (!selected) return;
    const opt = fiberglass.options.find(o => o.id === selected);
    addItem({
      id: `fiberglass-${opt.id}-${Date.now()}`,
      name: `Large Fiberglass Prop — ${opt.name}`,
      price: 0,
      quotePending: true,
      quantity: 1,
      category: 'props',
      image: opt.image,
      hours: 1,
    });
    toast.success(`${opt.name} Prop added — Quote Pending`);
    setSelected(null);
  };

  return (
    <div className="mb-12">
      <h3 className="font-display text-xl md:text-2xl text-white mb-1">Large Fiberglass Props</h3>
      <p className="text-white/40 text-xs mb-4 flex items-center gap-2">
        <ChevronLeft className="w-3 h-3" /> Select a prop below — only 1 of each available <ChevronRight className="w-3 h-3" />
      </p>

      <div className="relative">
        <button onClick={() => scroll(-1)} className="absolute -left-3 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-background/80 border border-white/10 flex items-center justify-center hover:bg-background transition-colors">
          <ChevronLeft className="w-4 h-4 text-white/70" />
        </button>
        <div ref={scrollRef} className="flex gap-4 overflow-x-auto scrollbar-hide pb-2 scroll-smooth">
          {fiberglass.options.map((opt) => (
            <div
              key={opt.id}
              className={`flex-shrink-0 w-52 cursor-pointer group transition-all duration-300 ${selected === opt.id ? 'ring-2 ring-gold' : ''}`}
              onClick={() => setSelected(selected === opt.id ? null : opt.id)}
              data-testid={`prop-${opt.id}`}
            >
              <div className="relative aspect-square overflow-hidden">
                <img src={opt.image} alt={opt.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <p className="absolute bottom-3 left-3 text-white text-sm font-medium">{opt.name}</p>
                {selected === opt.id && (
                  <div className="absolute top-3 right-3 w-6 h-6 bg-gold flex items-center justify-center rounded-full">
                    <Check className="w-3.5 h-3.5 text-background" />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        <button onClick={() => scroll(1)} className="absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-background/80 border border-white/10 flex items-center justify-center hover:bg-background transition-colors">
          <ChevronRight className="w-4 h-4 text-white/70" />
        </button>
      </div>

      <div className="flex items-center gap-4 mt-4">
        <span className="text-white/50 text-sm italic">Pricing upon request</span>
        <button
          onClick={handleAdd}
          disabled={!selected}
          className={`px-5 py-2.5 text-xs font-semibold uppercase tracking-wide transition-colors ${selected ? 'bg-gold text-background hover:bg-gold-light' : 'bg-white/10 text-white/30 cursor-not-allowed'}`}
          data-testid="add-fiberglass"
        >
          <Plus className="w-3 h-3 inline mr-1" /> {selected ? `Request Quote — ${fiberglass.options.find(o => o.id === selected)?.name}` : 'Select a Prop'}
        </button>
      </div>
    </div>
  );
}

function PackageCard({ item }) {
  const { addItem } = useCart();
  const [packs, setPacks] = useState(0);
  const total = item.basePrice + (item.addonPrice * packs);
  const unitCount = item.baseCount + (item.addonCount * packs);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="group"
      data-testid={`package-${item.id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden mb-4">
        <img src={item.image} alt={item.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute top-4 right-4 bg-background/80 backdrop-blur-sm px-3 py-1.5 border border-white/10">
          <span className="text-gold text-sm font-medium">{fmt(total)}</span>
        </div>
      </div>

      <h3 className="font-display text-xl md:text-2xl text-white group-hover:text-gold transition-colors duration-300 mb-2">
        {item.name}
      </h3>
      <p className="text-white/50 text-sm mb-3">
        Min {item.baseCount} = {fmt(item.basePrice)} | Add-on pack of {item.addonCount} = +{fmt(item.addonPrice)}
      </p>

      <div className="flex items-center gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2 bg-surface/60 border border-white/10 px-3 py-1.5">
          <span className="text-white/50 text-xs">Extra packs:</span>
          <button onClick={() => setPacks(Math.max(0, packs - 1))} className="text-white/50 hover:text-white"><Minus className="w-3 h-3" /></button>
          <span className="text-white text-sm w-6 text-center">{packs}</span>
          <button onClick={() => setPacks(packs + 1)} className="text-white/50 hover:text-white"><Plus className="w-3 h-3" /></button>
        </div>
        <span className="text-white/40 text-xs">{unitCount} total units</span>
      </div>

      <button
        onClick={() => {
          addItem({ id: `${item.id}-${Date.now()}`, name: `${item.name} (${unitCount} units)`, price: total, category: 'props', image: item.image, hours: 1 });
          toast.success(`${item.name} added to estimate`);
          setPacks(0);
        }}
        className="inline-flex items-center gap-2 px-4 py-2 bg-gold text-background text-xs font-semibold uppercase tracking-wide hover:bg-gold-light transition-colors duration-300"
        data-testid={`add-${item.id}`}
      >
        <Plus className="w-3 h-3" /> Add to Estimate
      </button>
    </motion.div>
  );
}

export function PropsSection() {
  const { addItem } = useCart();
  const { lighting, trees, other } = animalData.props;

  return (
    <section data-testid="props-section" className="py-24 md:py-32 px-6 md:px-12 bg-background">
      <div className="max-w-[1400px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <p className="text-xs tracking-[0.3em] uppercase text-gold mb-4">Theme Enhancements</p>
          <h2 className="font-display text-4xl md:text-6xl text-white uppercase tracking-wide mb-3">
            Props & Decor
          </h2>
          <p className="text-white/50 text-base md:text-lg max-w-xl">
            Transform your venue with our collection of high-quality props, custom signage, lighting and botanical installations.
          </p>
        </motion.div>

        {/* Fiberglass Slider */}
        <FiberglassSlider />

        {/* Lighting & Trees — Full Size Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <PackageCard item={lighting} />
          <PackageCard item={trees} />
        </div>

        {/* Other Props */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {other.map((prop, i) => (
            <motion.div
              key={prop.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="group"
              data-testid={`card-${prop.id}`}
            >
              <div className="relative aspect-[4/3] overflow-hidden mb-3">
                <img src={prop.image} alt={prop.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute top-3 right-3 bg-background/80 backdrop-blur-sm px-2 py-1 border border-white/10">
                  <span className="text-gold text-xs font-medium">{prop.priceNote || fmt(prop.price)}</span>
                </div>
              </div>
              <h4 className="text-white text-sm font-medium mb-2">{prop.name}</h4>
              <button
                onClick={() => {
                  addItem({ ...prop, hours: 1 });
                  toast.success(`${prop.name} added to estimate`);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gold text-background text-xs font-semibold uppercase tracking-wide hover:bg-gold-light transition-colors"
                data-testid={`add-${prop.id}`}
              >
                <Plus className="w-3 h-3" /> Add
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
